import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import type { ModalState, ToastMsg, Product } from "../types";

export interface UIContextValue {
  /* toasts */
  toasts: ToastMsg[];
  toast: (m: string) => void;
  /* confirm modal */
  modal: ModalState | null;
  setModal: (m: ModalState | null) => void;
  /* layout flags */
  sideOpen: boolean;
  setSideOpen: React.Dispatch<React.SetStateAction<boolean>>;
  cartOpen: boolean;
  setCartOpen: React.Dispatch<React.SetStateAction<boolean>>;
  darkMode: boolean;
  setDarkMode: React.Dispatch<React.SetStateAction<boolean>>;
  /* quick-view product modal */
  quickView: Product | null;
  setQuickView: (p: Product | null) => void;
  /* loading screen between routes */
  pageLoading: boolean;
  setPageLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const UIContext = createContext<UIContextValue | null>(null);

export function UIProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastMsg[]>([]);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [sideOpen, setSideOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [quickView, setQuickView] = useState<Product | null>(null);
  const [pageLoading, setPageLoading] = useState(false);

  const tid = useRef(0);
  const toast = useCallback((m: string) => {
    const id = ++tid.current;
    setToasts((ts) => [...ts, { id, m }]);
    setTimeout(() => setToasts((ts) => ts.filter((x) => x.id !== id)), 3000);
  }, []);

  const value: UIContextValue = {
    toasts, toast,
    modal, setModal,
    sideOpen, setSideOpen,
    cartOpen, setCartOpen,
    darkMode, setDarkMode,
    quickView, setQuickView,
    pageLoading, setPageLoading,
  };

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
}

export function useUI(): UIContextValue {
  const v = useContext(UIContext);
  if (!v) throw new Error("useUI must be used inside <UIProvider>");
  return v;
}